/**
 * Money, precision and rounding (plan §5.8).
 *
 * Every JOD figure the platform stores, adds or compares goes through here, so the arithmetic is
 * `decimal.js` end to end and never IEEE doubles: `0.1 + 0.2` is 0.3 in this package. Storage is
 * `numeric(14, 3)`, which `postgres` hands back as a *string*; `fromDb` is the only place that string
 * becomes a number and `toDb` the only place a number becomes one again.
 */
import { Decimal } from 'decimal.js';
/** 28 significant digits with commercial rounding, as the plan fixes it for the whole engine. */
export const Money = Decimal.clone({ precision: 28, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -9, toExpPos: 21 });
/**
 * The largest decimal exponent a result may carry. Nothing this platform prices comes near it (the
 * biggest amount a contract accepts is under 1e8), and it stops a formula such as `10 ^ 10 ^ 10` —
 * which `decimal.js` answers instantly and correctly — from then being expanded into a ten-billion-digit
 * string somewhere downstream.
 */
export const MAX_DECIMAL_EXPONENT = 30;
export const isWithinRange = (value) => value.isFinite() && Math.abs(value.e) <= MAX_DECIMAL_EXPONENT;
/** JOD is recorded to 0.001 (PP §7.1). */
export const MONEY_SCALE = 3;
/** Rates, counts and flags carry more decimals so a chain of fractions does not lose the last digit. */
export const DIMENSIONLESS_SCALE = 6;
export const decimal = (value) => new Money(value);
/**
 * The scale an equation's result is rounded to at its boundary (plan §5.8): JOD and every other
 * dimensioned unit to three decimals, dimensionless fractions/counts/flags to six.
 */
export const scaleForUnit = (unit) => unit === 'fraction' || unit === 'count' || unit === 'flag' ? DIMENSIONLESS_SCALE : MONEY_SCALE;
/** `numeric(14, 3)` arrives as a string; `null` stays `null` so "not recorded" never becomes zero. */
export const fromDb = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = new Money(value);
    return parsed.isFinite() ? parsed.toNumber() : null;
};
/** The string a `numeric` column is written with — never a float literal. */
export const toDb = (value, scale = MONEY_SCALE) => value === null || value === undefined ? null : new Money(value).toFixed(scale);
export const add = (a, b) => new Money(a).plus(b).toNumber();
export const subtract = (a, b) => new Money(a).minus(b).toNumber();
export const multiply = (a, b) => new Money(a).times(b).toNumber();
export const divide = (a, b) => {
    const divisor = new Money(b);
    return divisor.isZero() ? null : new Money(a).dividedBy(divisor).toNumber();
};
/** `-1 | 0 | 1`, so a comparison never depends on a float epsilon. */
export const compare = (a, b) => new Money(a).comparedTo(b);
export const roundToScale = (value, scale = MONEY_SCALE) => new Money(value).toDecimalPlaces(scale, Decimal.ROUND_HALF_UP).toNumber();
/** Sums a list the way the cost pipeline does — left to right, exact, empty list is zero. */
export const sum = (values) => values.reduce((total, value) => total.plus(value), new Money(0)).toNumber();
//# sourceMappingURL=money.js.map